import React, { createContext, useContext, useState, ReactNode } from 'react';

interface MusicFile {
  _id: string;
  name: string;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
}

interface MusicFileContextType {
  musicFiles: MusicFile[];
  loading: boolean;
  error: string | null;
  fetchMusicFiles: () => Promise<void>;
  uploadMusicFile: (file: File) => Promise<MusicFile | null>;
  deleteMusicFile: (id: string) => Promise<boolean>;
}

const MusicFileContext = createContext<MusicFileContextType | undefined>(undefined);

export const useMusicFile = () => {
  const context = useContext(MusicFileContext);
  if (!context) {
    throw new Error('useMusicFile must be used within a MusicFileProvider');
  }
  return context;
};

interface MusicFileProviderProps {
  children: ReactNode;
}

export const MusicFileProvider: React.FC<MusicFileProviderProps> = ({ children }) => {
  const [musicFiles, setMusicFiles] = useState<MusicFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMusicFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch('/api/music-files', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMusicFiles(data.data || []);
      } else {
        throw new Error('Müzik dosyaları getirilemedi');
      }
    } catch (err) {
      console.error('Music files fetch error:', err);
      setError(err instanceof Error ? err.message : 'Müzik dosyaları getirilemedi');
    } finally {
      setLoading(false);
    }
  };

  const uploadMusicFile = async (file: File): Promise<MusicFile | null> => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('music', file);

      const response = await fetch('/api/music-files', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        // Refresh the music files list
        await fetchMusicFiles();
        return data.data;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Müzik dosyası yüklenemedi');
      }
    } catch (err) {
      console.error('Music file upload error:', err);
      setError(err instanceof Error ? err.message : 'Müzik dosyası yüklenemedi');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteMusicFile = async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/music-files/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Remove from local state
        setMusicFiles(prev => prev.filter(file => file._id !== id));
        return true;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Müzik dosyası silinemedi');
      }
    } catch (err) {
      console.error('Music file delete error:', err);
      setError(err instanceof Error ? err.message : 'Müzik dosyası silinemedi');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const value: MusicFileContextType = {
    musicFiles,
    loading,
    error,
    fetchMusicFiles,
    uploadMusicFile,
    deleteMusicFile
  };

  return (
    <MusicFileContext.Provider value={value}>
      {children}
    </MusicFileContext.Provider>
  );
};
